import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getMetadata } from '../../utils/encryptedStorage';
import aiAutomationApi from '../../utils/aiAutomationApi';
import InputBox from '../../components/InputBox';

const InterviewerSettings = () => {
  const [interviewers, setInterviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Single modal state
  const [showModal, setShowModal] = useState(false);

  // Form state (inline in modal)
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', designation: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formExpanded, setFormExpanded] = useState(false);

  // Confirm action state (shows confirm section, hides main content)
  const [confirmAction, setConfirmAction] = useState(null);

  // Schedule modal state
  const [scheduleModal, setScheduleModal] = useState({ open: false, interviewer: null });
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const getUserId = () => {
    const metadata = getMetadata();
    return metadata?.uid || metadata?.userId || metadata?.id || '';
  };

  const fetchSchedule = async (interviewer) => {
    setScheduleModal({ open: true, interviewer });
    setScheduleLoading(true);
    setScheduleData(null);
    try {
      const data = await aiAutomationApi.get(`/interviews/slots/global?interviewer_id=${interviewer.id}`);
      setScheduleData(data);
    } catch (err) {
      console.error('Failed to load schedule:', err);
      setScheduleData({ error: err.message });
    } finally {
      setScheduleLoading(false);
    }
  };

  const formatTime12 = (time) => {
    if (!time) return time;
    try {
      const [h, m] = time.split(':').map(Number);
      const ampm = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
    } catch { return time; }
  };

  const fetchInterviewers = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await aiAutomationApi.get(`/interviewers?user_id=${userId}`);
      if (data.success) {
        setInterviewers(data.interviewers || []);
      } else {
        setError('Failed to load interviewers');
      }
    } catch (err) {
      setError(err.message || 'Failed to load interviewers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInterviewers();
  }, [fetchInterviewers]);

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', designation: '' });
    setEditingId(null);
    setFormExpanded(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setConfirmAction(null);
    resetForm();
    setFormExpanded(false);
    setError(null);
    setSuccessMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and Email are required');
      return;
    }
    setFormLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      if (editingId) {
        await aiAutomationApi.put(`/interviewers/${editingId}`, formData);
        setSuccessMsg('Interviewer updated successfully');
      } else {
        const metadata = getMetadata();
        const userId = String(metadata?.uid || metadata?.userId || metadata?.id || '');
        const cid = String(metadata?.cid || '');
        await aiAutomationApi.post('/interviewers', {
          name: String(formData.name || ''),
          email: String(formData.email || ''),
          phone: formData.phone ? String(formData.phone) : null,
          designation: formData.designation ? String(formData.designation) : null,
          user_id: userId || null,
          cid: cid || null,
        });
        setSuccessMsg('Interviewer added successfully');
      }
      resetForm();
      fetchInterviewers();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (interviewer) => {
    setEditingId(interviewer.id);
    setFormData({
      name: interviewer.name,
      email: interviewer.email,
      phone: interviewer.phone || '',
      designation: interviewer.designation || '',
    });
    setFormExpanded(true);
    setError(null);
    setSuccessMsg(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleToggleStatus = async (interviewer) => {
    setError(null);
    setSuccessMsg(null);
    setConfirmAction(null);
    try {
      const data = await aiAutomationApi.put(`/interviewers/${interviewer.id}/toggle-status`, {});
      if (data.warning) setError(data.warning);
      setSuccessMsg(data.message);
      fetchInterviewers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (interviewer) => {
    setError(null);
    setSuccessMsg(null);
    setConfirmAction(null);
    try {
      await aiAutomationApi.delete(`/interviewers/${interviewer.id}`);
      setSuccessMsg('Interviewer deleted successfully');
      fetchInterviewers();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const activeCount = interviewers.filter((i) => i.is_active).length;

  return (
    <>
      {/* ─── Settings Section Card (matches MailSettingsPage style) ─── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Interview Team</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Manage interviewers for scheduling interviews
            </p>
          </div>
          <button
            onClick={() => { setShowModal(true); resetForm(); setError(null); setSuccessMsg(null); setConfirmAction(null); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
            </svg>
            Manage Interviewers
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600"></div>
              <span className="text-sm">Loading interviewers...</span>
            </div>
          ) : interviewers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No interviewers configured. Click "Manage Interviewers" to add your first interviewer.
            </p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {interviewers.slice(0, 5).map((interviewer, idx) => (
                  <div
                    key={interviewer.id}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white border-2 border-white dark:border-gray-800 ${
                      interviewer.is_active ? 'bg-blue-600' : 'bg-gray-400'
                    }`}
                    style={{ zIndex: 5 - idx }}
                    title={`${interviewer.name}${interviewer.is_active ? '' : ' (Inactive)'}`}
                  >
                    {interviewer.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                ))}
                {interviewers.length > 5 && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 border-2 border-white dark:border-gray-800">
                    +{interviewers.length - 5}
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-900 dark:text-white">{activeCount}</span> active
                {interviewers.length > activeCount && (
                  <span className="text-gray-400 dark:text-gray-500"> · {interviewers.length - activeCount} inactive</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Single Large Modal ─── */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={handleCloseModal}
        >
          <div
            className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Manage Interview Team</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {interviewers.length} interviewer{interviewers.length !== 1 ? 's' : ''} · {activeCount} active
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 dark:hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Show confirm view OR main content */}
            {confirmAction ? (
              /* ─── Confirm Section (replaces main content) ─── */
              <div className="flex-1 flex items-center justify-center p-10">
                <div className="text-center max-w-sm">
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center ${
                    confirmAction.type === 'delete' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    <svg className={`w-8 h-8 ${confirmAction.type === 'delete' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {confirmAction.type === 'delete' ? 'Delete Interviewer?' : 'Deactivate Interviewer?'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    {confirmAction.type === 'delete'
                      ? `"${confirmAction.interviewer.name}" will be permanently removed. This action cannot be undone.`
                      : `"${confirmAction.interviewer.name}" will be removed from all future scheduling options.`}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setConfirmAction(null)}
                      className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmAction.type === 'delete' ? handleDelete(confirmAction.interviewer) : handleToggleStatus(confirmAction.interviewer)}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
                        confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'
                      }`}
                    >
                      {confirmAction.type === 'delete' ? 'Delete' : 'Deactivate'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ─── Main Content: Form + Table ─── */
              <div className="flex-1 overflow-y-auto">
                {/* Messages */}
                {(error || successMsg) && (
                  <div className="px-6 pt-4">
                    {error && (
                      <div className="p-4 rounded-lg flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                      </div>
                    )}
                    {successMsg && (
                      <div className="p-4 rounded-lg flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-green-700 dark:text-green-300">{successMsg}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Add/Edit Form Section - Collapsible */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                  {/* Collapsible Header */}
                  <button
                    type="button"
                    onClick={() => setFormExpanded(!formExpanded)}
                    className="w-full px-6 py-4 flex items-center justify-between bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d={editingId ? "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" : "M12 4.5v15m7.5-7.5h-15"} />
                      </svg>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {editingId ? 'Edit Interviewer' : 'Add New Interviewer'}
                      </span>
                    </div>
                    <svg 
                      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${formExpanded ? 'rotate-180' : ''}`} 
                      fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>

                  {/* Collapsible Content */}
                  <div className={`overflow-hidden transition-all duration-300 ${formExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <form onSubmit={handleSubmit} className="px-6 pt-4 pb-5 bg-blue-50/30 dark:bg-blue-900/5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputBox
                          label="Full Name"
                          placeholder="Enter name"
                          value={formData.name}
                          onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                          required
                        />
                        <InputBox
                          label="Email Address"
                          type="email"
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                          required
                        />
                        <InputBox
                          label="Phone Number"
                          type="tel"
                          placeholder="Optional (max 10 digits)"
                          value={formData.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData((f) => ({ ...f, phone: val }));
                          }}
                        />
                        <InputBox
                          label="Designation"
                          placeholder="e.g. Senior Engineer"
                          value={formData.designation}
                          onChange={(e) => setFormData((f) => ({ ...f, designation: e.target.value }))}
                        />
                      </div>
                      <div className="flex items-center justify-end gap-3 mt-4">
                        {editingId && (
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            Cancel Edit
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={formLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                          {formLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              {editingId ? 'Saving...' : 'Adding...'}
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d={editingId ? "M4.5 12.75l6 6 9-13.5" : "M12 4.5v15m7.5-7.5h-15"} />
                              </svg>
                              {editingId ? 'Save Changes' : 'Add Interviewer'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Interviewers Table */}
                <div className="px-6 py-5">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                    Interview Team ({interviewers.length})
                  </h4>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
                      <span className="ml-3 text-gray-500 dark:text-gray-400">Loading interviewers...</span>
                    </div>
                  ) : interviewers.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No interviewers yet. Use the form above to add your first interviewer.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Name</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Email</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Designation</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">Status</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {interviewers.map((interviewer) => (
                            <tr
                              key={interviewer.id}
                              className={`bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                                !interviewer.is_active ? 'opacity-60' : ''
                              } ${editingId === interviewer.id ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white ${
                                    interviewer.is_active ? 'bg-blue-600' : 'bg-gray-400'
                                  }`}>
                                    {interviewer.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span className="font-medium text-gray-900 dark:text-white">{interviewer.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{interviewer.email}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{interviewer.designation || '—'}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  interviewer.is_active
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                  {interviewer.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => fetchSchedule(interviewer)}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                    title="View Schedule"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleEdit(interviewer)}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    title="Edit"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => interviewer.is_active ? setConfirmAction({ type: 'deactivate', interviewer }) : handleToggleStatus(interviewer)}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                      interviewer.is_active
                                        ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                                        : 'text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    }`}
                                    title={interviewer.is_active ? 'Deactivate' : 'Activate'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      {interviewer.is_active ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                      ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      )}
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => setConfirmAction({ type: 'delete', interviewer })}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Interviewer Schedule Modal */}
      <ScheduleModal
        open={scheduleModal.open}
        interviewer={scheduleModal.interviewer}
        data={scheduleData}
        loading={scheduleLoading}
        onClose={() => { setScheduleModal({ open: false, interviewer: null }); setScheduleData(null); }}
        formatTime12={formatTime12}
      />
    </>
  );
};

// Schedule Modal Component
const ScheduleModal = ({ open, interviewer, data, loading, onClose, formatTime12 }) => {
  if (!open || !interviewer) return null;

  const todayStr = new Date().toISOString().split('T')[0];

  // Parse schedule data grouped by date
  const slotsByDate = {};
  let totalSlots = 0;
  let bookedCount = 0;
  let availableCount = 0;

  if (data && !data.error) {
    const allSlots = [
      ...(data.booked_slots || []).map(s => ({ ...s, status: 'booked' })),
      ...(data.available_slots || []).map(s => ({ ...s, status: 'available' })),
    ];

    for (const s of allSlots) {
      if (!s.date || s.date < todayStr) continue;
      if (!slotsByDate[s.date]) slotsByDate[s.date] = [];
      slotsByDate[s.date].push(s);
      totalSlots++;
      if (s.status === 'booked') bookedCount++;
      else availableCount++;
    }

    // Sort slots within each date
    for (const date of Object.keys(slotsByDate)) {
      slotsByDate[date].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    }
  }

  const sortedDates = Object.keys(slotsByDate).sort();

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-blue-600">
          <div>
            <h3 className="text-lg font-semibold text-white">{interviewer.name}'s Schedule</h3>
            <p className="text-sm text-indigo-100">{interviewer.email}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary */}
        {!loading && !data?.error && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              <strong>{totalSlots}</strong> total slots
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
              <span className="text-green-700 dark:text-green-400">{availableCount} open</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />
              <span className="text-orange-700 dark:text-orange-400">{bookedCount} booked</span>
            </span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <svg className="w-6 h-6 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="ml-3 text-sm text-gray-500">Loading schedule...</span>
            </div>
          )}

          {data?.error && (
            <div className="text-center py-12 text-red-500">
              <p>Failed to load schedule: {data.error}</p>
            </div>
          )}

          {!loading && !data?.error && sortedDates.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">No upcoming interview slots</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Schedule interviews from the chat to create slots</p>
            </div>
          )}

          {!loading && !data?.error && sortedDates.length > 0 && (
            <div className="space-y-4">
              {sortedDates.map((date) => {
                const slots = slotsByDate[date];
                const isToday = date === todayStr;
                const dateBooked = slots.filter(s => s.status === 'booked').length;
                const dateAvail = slots.filter(s => s.status === 'available').length;

                return (
                  <div key={date} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Date Header */}
                    <div className={`px-4 py-2.5 flex items-center justify-between ${
                      isToday
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-800'
                        : 'bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isToday ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          {formatDate(date)}
                        </span>
                        {isToday && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-600 text-white">TODAY</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>{dateAvail} open</span>
                        <span>{dateBooked} booked</span>
                      </div>
                    </div>

                    {/* Slot Grid */}
                    <div className="px-4 py-3 flex flex-wrap gap-2">
                      {slots.map((slot, idx) => {
                        const isBooked = slot.status === 'booked';
                        return (
                          <div
                            key={idx}
                            className={`relative group px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                              isBooked
                                ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300'
                                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${isBooked ? 'bg-orange-500' : 'bg-green-500'}`} />
                              <span>{formatTime12(slot.time)}</span>
                            </div>
                            {slot.session_title && (
                              <div className="text-[10px] mt-0.5 opacity-70">{slot.session_title}</div>
                            )}
                            {slot.candidate_name && (
                              <div className="text-[10px] mt-0.5 opacity-70">{slot.candidate_name}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InterviewerSettings;
